import { Component, OnDestroy, OnInit } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { take, takeUntil } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';
import { tenantClass } from '../../../../util/state-selectors';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { DetailsQueries } from '../../../details/queries/details.queries';
import { ImageMetaData, BackLink, BackLinkState, AuctionItemState } from 'remarketing-angular-library';
import { InventoryItemDataService } from '../../../sell-shared/services/inventory-item-data.service';
import {
  BannerComponent,
  BannerConfig
} from '../../../sell-shared/components/banner/banner.component';
import { TranslocoService } from '@jsverse/transloco';
import { checkMobile } from '../../../../../app/shared/utils/util';
import { AnalyticsService } from 'smartauction-frontend-micro-library';
import { CommonModule } from '@angular/common';
import { InventoryItemCardComponent } from '../inventory-item-card/inventory-item-card.component';

enum ManageInventoryHeader {
  MANAGE_OFFERS = 'MANAGE_OFFERS',
  MANAGE_QUESTIONS = 'MANAGE_QUESTIONS',
  MANAGE_DETAILS = 'MANAGE_DETAILS'
}

class Header {
  text: string;
  type: ManageInventoryHeader;
}
@Component({
    selector: 'lib-inventory-item-shell',
    templateUrl: './inventory-item-shell.component.html',
    styleUrls: ['./inventory-item-shell.component.scss'],
    providers: [InventoryItemDataService],
    imports: [CommonModule, RouterModule, InventoryItemCardComponent, BannerComponent]
})
export class InventoryItemShellComponent implements OnInit, OnDestroy {
  readonly activeClass = 'inventory-item-nav__link--active';
  @Select(tenantClass) tenantClass$: Observable<string>;
  @Select(AuctionItemState.auctionItem) auctionItem$: Observable<any>;
  @Select(DetailsQueries.images) images$: Observable<ImageMetaData[]>;
  @Select(BackLinkState.currentBackLink('Back to Manage Inventory', '/inventory')) backLink$: Observable<BackLink>;
  private destroyed$ = new Subject();

  translations = {
    inventoryItemHeader: {
      manage: '',
      offers: '',
      questions: '',
      details: ''
    },
    sellModule: {
      ariaLabelInventoryNavigation: '',
      ariaLabelnventoryDetail: '',
      ariaLabelInventoryOffers: '',
      ariaLabelInventoryQuestions: '',
      textDetails: '',
      textOffers: '',
      textQuestions: ''
    },
    backlink: '',
    backLinkMobile: ''
  };

  header = {
    text: '',
    type: null
  } as Header;

  ManageInventoryHeader = ManageInventoryHeader;
  currentTabSelection: ManageInventoryHeader = null;
  prev: string;

  showVehicleCard = false;

  showErrorBanner = true;
  banner: BannerConfig = {
    text: '',
    mode: 'error',
    showIcon: true,
    showUndo: false,
    showErrorIcon: true,
    showSuccessIcon: false
  };

  globalErrorState$: Observable<{ message: string; showErrorBanner: boolean }> = this.inventoryItemDataService
    .globalerrorState;

  get isMobile() {
    return checkMobile();
  }

  constructor(
    private store: Store,
    private route: ActivatedRoute,
    private router: Router,
    private readonly inventoryItemDataService: InventoryItemDataService,
    private readonly translocoService: TranslocoService,
    private readonly analyticsService: AnalyticsService
  ) {
    if (this.router.currentNavigation() && this.router.currentNavigation().previousNavigation) {
      this.prev = this.router.currentNavigation().previousNavigation.finalUrl.toString();
      sessionStorage.setItem('sell_inventory_previous_link', this.prev);
    } else {
      this.prev = sessionStorage.getItem('sell_inventory_previous_link');
    }
  }

  ngOnInit() {
    this.initializeHeader(this.route.snapshot.firstChild ? this.route.snapshot.firstChild.routeConfig.path : '');
    this.getTranslations();
    this.inventoryItemDataService
      .listen$(this.route.snapshot.paramMap.get('inventoryItemUuid'))
      .pipe(takeUntil(this.destroyed$))
      .subscribe();

    this.globalErrorState$.pipe(takeUntil(this.destroyed$)).subscribe(globalErrorState => {
      this.showErrorBanner = globalErrorState.showErrorBanner;
      this.banner.text = globalErrorState.message;
    });
  }

  analyticsClickBackEvent(backLinkUri: string) {
    let eventName;
    if (backLinkUri === '/dashboard') {
      eventName = 'Back to Home Page';
    } else if (backLinkUri.includes('arbitration')) {
      eventName = 'Back to Arbitration Request';
    } else {
      eventName = 'Back To Manage Inventory';
    }
    this.analyticsService.trackEvent('link', eventName);
  }

  getBackLinkText(translation) {
    this.backLink$.pipe(take(1)).subscribe(backlink => {
      if (backlink.targetUri === '/dashboard') {
        this.translations.backlink = translation.backtoHomepage;
      } else if (backlink.targetUri.includes('arbitration')) {
        this.translations.backlink = translation.backtoArbitrationRequest;
      } else {
        this.translations.backlink = translation.backtoManageInventory;
      }
    });
  }

  initializeHeader(nav) {
    const offersRegex = /offers/;
    const questionsRegex = /questions/;
    this.showVehicleCard = offersRegex.test(nav) || questionsRegex.test(nav);
    if (offersRegex.test(nav)) {
      this.updateHeader(ManageInventoryHeader.MANAGE_OFFERS);
    } else {
      if (questionsRegex.test(nav)) {
        this.updateHeader(ManageInventoryHeader.MANAGE_QUESTIONS);
      } else {
        this.updateHeader(ManageInventoryHeader.MANAGE_DETAILS);
      }
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  closeBanner() {
    this.inventoryItemDataService._globalErrorState.next({ message: null, showErrorBanner: false });
    this.inventoryItemDataService._globalOfferError.next(false);
  }

  updateHeader(selection: ManageInventoryHeader) {
    switch (selection) {
      case ManageInventoryHeader.MANAGE_OFFERS:
        this.header = {
          text: this.translations.inventoryItemHeader.offers,
          type: ManageInventoryHeader.MANAGE_OFFERS
        };
        this.currentTabSelection = ManageInventoryHeader.MANAGE_OFFERS;
        break;
      case ManageInventoryHeader.MANAGE_QUESTIONS:
        this.header = {
          text: this.translations.inventoryItemHeader.questions,
          type: ManageInventoryHeader.MANAGE_QUESTIONS
        };
        this.currentTabSelection = ManageInventoryHeader.MANAGE_QUESTIONS;
        break;
      case ManageInventoryHeader.MANAGE_DETAILS:
        this.header = {
          text: this.translations.inventoryItemHeader.details,
          type: ManageInventoryHeader.MANAGE_DETAILS
        };
        this.currentTabSelection = ManageInventoryHeader.MANAGE_DETAILS;
        break;
    }
  }

  getTranslations() {
    const keyParams = {
      'UIComponents.inventoryItemHeaderTranslations': {},
      'remarketingLibrary.backLinks': {},
      'sellModule.inventoryItem.inventoryItemShell': {},
      'search.editPreferences': {}
    };

    this.translocoService
      .selectTranslateObject(keyParams)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(translate => {
        this.translations.inventoryItemHeader.details = translate[0].details;
        this.translations.inventoryItemHeader.offers = translate[0].offers;
        this.translations.inventoryItemHeader.questions = translate[0].questions;
        this.translations.backLinkMobile = translate[3].backLink;
        this.translations.sellModule.ariaLabelInventoryNavigation = translate[2].ariaLabelInventoryNavigation;
        this.translations.sellModule.ariaLabelnventoryDetail = translate[2].ariaLabelnventoryDetail;
        this.translations.sellModule.ariaLabelInventoryOffers = translate[2].ariaLabelInventoryOffers;
        this.translations.sellModule.ariaLabelInventoryQuestions = translate[2].ariaLabelInventoryQuestions;
        this.translations.sellModule.textDetails = translate[2].textDetails;
        this.translations.sellModule.textOffers = translate[2].textOffers;
        this.translations.sellModule.textQuestions = translate[2].textQuestions;
        this.updateHeader(this.currentTabSelection);
        this.getBackLinkText(translate[1]);
      });
  }
}
